import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Mail, MessageCircle } from 'lucide-react';
import { navGroups, socialLinks } from '../data/homeContent';
import { LOGO_LIGHT_URL } from '../data/brandAssets';
import { trackEvent, analyticsEvents, contactInfo } from '../data/mock';
import { MegaMenuPanel, megaItems } from './MegaMenu';

const DesktopGroup = ({ group, openKey, setOpenKey }) => {
  const isOpen = openKey === group.label;
  if (!group.items && !group.mega) {
    return (
      <Link to={group.to} data-testid={group.testId} className="hv-nav-link">
        {group.label}
      </Link>
    );
  }
  return (
    <div className="relative" onMouseEnter={() => setOpenKey(group.label)} onMouseLeave={() => { if (!group.mega) setOpenKey(null); }}>
      <button
        type="button"
        data-testid={group.testId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => setOpenKey(isOpen ? null : group.label)}
        className="hv-nav-link inline-flex items-center gap-1"
      >
        {group.label}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {isOpen && !group.mega && (
        <div className="hv-nav-dropdown absolute left-0 top-full pt-3" data-testid={`${group.testId}-menu`}>
          <div className="min-w-[300px] bg-[#FFFEFA] border border-[#DEDFD3] rounded-[4px] shadow-[0_12px_32px_rgba(25,57,43,0.12)] p-2">
            {group.items.map((item) => (
              <Link key={item.to} to={item.to} data-testid={`nav-item-${item.to.replace(/\//g, '-').slice(1)}`} onClick={() => setOpenKey(null)} className="hv-nav-item block px-4 py-3 rounded-[4px]">
                <span className="block text-[15px] font-medium text-[#19392B]">{item.label}</span>
                {item.hint && <span className="block text-sm text-[#647064] mt-0.5">{item.hint}</span>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileMenuOverlay = ({ isOpen, onClose }) => {
  const [expanded, setExpanded] = useState(null);
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);
  if (!isOpen) return null;
  return (
    <div data-testid="mobile-menu-overlay" className="fixed inset-0 z-[9999] bg-[#153B2B]/60 lg:hidden" onClick={onClose}>
      <div className="hv-mobile-panel hv-dark ml-auto h-full w-[min(100%,380px)] bg-[#153B2B] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 h-[72px] border-b border-[#2A5A45]">
          <img src={LOGO_LIGHT_URL} alt="AgrarKapital" className="h-10 w-auto" />
          <button type="button" onClick={onClose} aria-label="Menü schließen" data-testid="navigation-mobile-menu-close" className="hv-icon-btn">
            <X className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Link to="/bewertung" onClick={onClose} data-testid="mobile-menu-cta" className="hv-btn-accent flex items-center justify-center h-[52px] rounded-[4px] bg-[#DFC590] text-[#19392B] font-semibold mb-5">
            Kostenlose Bewertung
          </Link>
          <nav aria-label="Mobile Navigation" className="divide-y divide-[#2A5A45]">
            {navGroups.map((group) => (
              <div key={group.label} className="py-1">
                {(group.items || group.mega) ? (
                  <>
                    <button
                      type="button"
                      aria-expanded={expanded === group.label}
                      data-testid={`mobile-${group.testId}`}
                      onClick={() => setExpanded(expanded === group.label ? null : group.label)}
                      className="hv-mobile-link w-full flex items-center justify-between py-3 text-left text-[17px] font-medium text-[#F8F6F0]"
                    >
                      {group.label}
                      <ChevronDown className={`w-5 h-5 text-[#DFC590] transition-transform ${expanded === group.label ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </button>
                    {expanded === group.label && (
                      <div className="pb-3 pl-3 space-y-1">
                        {megaItems(group).map((item) => (
                          <Link key={item.to} to={item.to} onClick={onClose} className="block py-2 text-[15px] text-[#C9D3C8]">{item.label}</Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={group.to} onClick={onClose} data-testid={`mobile-${group.testId}`} className="block py-3 text-[17px] font-medium text-[#F8F6F0]">{group.label}</Link>
                )}
              </div>
            ))}
          </nav>
        </div>
        <div className="px-5 py-5 border-t border-[#2A5A45] bg-[#0F2E22]">
          <a href={contactInfo.phoneLink} data-testid="mobile-menu-phone" className="flex items-center gap-3 text-[#F8F6F0] font-medium">
            <Phone className="w-5 h-5 text-[#DFC590]" aria-hidden="true" />{contactInfo.phone}
          </a>
          <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="flex items-center gap-3 text-[#F8F6F0] font-medium mt-3">
            <MessageCircle className="w-5 h-5 text-[#DFC590]" aria-hidden="true" />WhatsApp schreiben
          </a>
          <a href={contactInfo.emailLink} data-testid="mobile-menu-email" className="flex items-center gap-3 text-[#C9D3C8] text-sm mt-3"><Mail className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />{contactInfo.email}</a>
          <p className="text-sm text-[#A9B8A9] mt-2">Mo–Fr 9–17 Uhr</p>
          <div className="flex items-center gap-2 mt-4">
            {socialLinks.map((sl) => (
              <a key={sl.key} href={sl.url} target="_blank" rel="noopener noreferrer" aria-label={`AgrarKapital auf ${sl.label}`} data-testid={`mobile-menu-social-${sl.key}`} className="hv-icon-btn hv-icon-btn-light">
                <sl.icon className="w-4 h-4" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef(null);

  useEffect(() => { setMobileOpen(false); setOpenKey(null); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 480);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const close = (e) => { if (navRef.current && !navRef.current.contains(e.target)) setOpenKey(null); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpenKey(null); setMobileOpen(false); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', onKey); };
  }, []);

  const handlePhoneClick = () => {
    trackEvent(analyticsEvents.PHONE_CLICK, { location: 'nav' });
    window.location.href = contactInfo.phoneLink;
  };

  return (
    <>
      <div className="hv-topbar hv-dark hidden md:block bg-[#0F2E22] text-[#C9D3C8] border-b border-[#2A5A45]/60" data-testid="top-bar">
        <div className="container mx-auto px-4 flex items-center justify-between h-10 text-[13px]">
          <div className="flex items-center gap-6">
            <a href={contactInfo.phoneLink} data-testid="topbar-phone" onClick={() => trackEvent(analyticsEvents.PHONE_CLICK, { location: 'topbar' })} className="hv-link inline-flex items-center gap-2 text-[#F8F6F0]"><Phone className="w-3.5 h-3.5 text-[#DFC590]" aria-hidden="true" />{contactInfo.phone}</a>
            <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="topbar-whatsapp" onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: 'whatsapp', location: 'topbar' })} className="hv-link inline-flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-[#DFC590]" aria-hidden="true" />WhatsApp</a>
            <a href={contactInfo.emailLink} data-testid="topbar-email" className="hv-link inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#DFC590]" aria-hidden="true" />{contactInfo.email}</a>
            <span className="hidden xl:inline text-[#A9B8A9]">Mo–Fr 9–17 Uhr · Freising · deutschlandweit</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="hidden lg:inline mr-2 text-[#A9B8A9]">Folgen Sie uns</span>
            {socialLinks.map((sl) => (
              <a key={sl.key} href={sl.url} target="_blank" rel="noopener noreferrer" aria-label={`AgrarKapital auf ${sl.label}`} data-testid={`topbar-social-${sl.key}`} onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: sl.key, location: 'topbar' })} className="hv-icon-btn !w-8 !h-8 !min-h-0 text-[#F8F6F0]">
                <sl.icon className="w-3.5 h-3.5" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>
      <header ref={navRef} data-testid="main-navigation" className="hv-nav hv-dark sticky top-0 z-50 bg-[#153B2B] border-b border-[#2A5A45]/60" onMouseLeave={() => setOpenKey(null)}>
        <div className="container mx-auto px-4 relative">
          {navGroups.filter((g) => g.mega && openKey === g.label).map((g) => (
            <div key={g.label} className="hidden lg:block" onMouseEnter={() => setOpenKey(g.label)}>
              <MegaMenuPanel group={g} onClose={() => setOpenKey(null)} />
            </div>
          ))}
          <div className="flex items-center justify-between gap-6 h-[80px]">
            <Link to="/" data-testid="navigation-home" aria-label="AgrarKapital Startseite" className="flex items-center shrink-0">
              <img src={LOGO_LIGHT_URL} alt="AgrarKapital" className="h-12 w-auto" />
            </Link>

            <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center gap-1">
              {navGroups.map((group) => (
                <DesktopGroup key={group.label} group={group} openKey={openKey} setOpenKey={setOpenKey} />
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3 shrink-0">
              <button type="button" onClick={handlePhoneClick} data-testid="navigation-call" className="hv-link inline-flex items-center gap-2 px-3 h-[48px] text-[#F8F6F0] font-medium">
                <Phone className="w-4 h-4 text-[#DFC590]" aria-hidden="true" />
                {contactInfo.phone}
              </button>
              <button
                type="button"
                data-testid="navigation-start-valuation"
                onClick={() => { trackEvent(analyticsEvents.HERO_CTA_CLICK, { cta_type: 'nav' }); navigate('/bewertung'); }}
                className="hv-btn-accent inline-flex items-center justify-center h-[48px] px-5 rounded-[4px] bg-[#DFC590] hover:bg-[#E8D3A9] text-[#19392B] font-semibold"
              >
                Kostenlose Bewertung
              </button>
            </div>

            <div className="flex lg:hidden items-center gap-1">
              <button type="button" onClick={handlePhoneClick} aria-label="Anrufen" data-testid="navigation-mobile-call" className="hv-icon-btn">
                <Phone className="w-5 h-5" aria-hidden="true" />
              </button>
              <button type="button" onClick={() => setMobileOpen(true)} aria-label="Menü öffnen" data-testid="navigation-mobile-menu-open" className="hv-icon-btn">
                <Menu className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <MobileMenuOverlay isOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" aria-label="Per WhatsApp schreiben" title="Per WhatsApp schreiben" data-testid="floating-whatsapp" onClick={() => trackEvent(analyticsEvents.SOCIAL_CLICK, { platform: 'whatsapp', location: 'floating' })} className={`hv-float-wa fixed bottom-5 right-5 z-[60] inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#153B2B]/90 backdrop-blur text-[#F8F6F0] border border-[#DFC590]/40 shadow-[0_10px_24px_-12px_rgba(0,0,0,.6)] ${scrolled ? 'is-visible' : ''}`}>
        <MessageCircle className="w-5 h-5" aria-hidden="true" />
      </a>
    </>
  );
};

export { Navigation, MobileMenuOverlay };
export default Navigation;
